/**
 * AI Evaluation Panel — health ring, nudges, per-license readiness, bankability.
 * From FRONTEND_BUILD_SPEC §3.8.
 *
 * The evaluation payload comes from the dashboard (which already fetched it
 * for the hero and tab badge) — the panel must not refetch: one GET is one
 * paid LLM enhancement server-side, and two fetches let the badge and panel
 * disagree about the same nudge count.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, DollarSign, ChevronRight } from "lucide-react";
import { status, band, bandStyle, bandBar, bandText, bankabilityLabel } from "../../lib/semantics.ts";
import { HealthRing } from "../shared/HealthRing.tsx";
import { StatusPill } from "../shared/StatusPill.tsx";
import { ErrorMessage } from "../shared/ErrorMessage.tsx";
import { Tabs, tabPanelProps } from "../shared/Tabs.tsx";
import { GuidedTasks } from "../shared/GuidedTasks.tsx";
import { SHOW_BANKABILITY, visibleNudges } from "../../lib/constants.ts";

interface EvaluationPanelProps {
  projectId: string;
  evaluation: any;
  evaluationLoaded: boolean;
  /** Re-runs the dashboard's evaluation fetch (also refreshes hero + badge). */
  onRetry?: () => void;
}

export function EvaluationPanel({ projectId, evaluation, evaluationLoaded, onRetry }: EvaluationPanelProps) {
  const [activeTab, setActiveTab] = useState("nudges");

  // The task queue is independent of the evaluation payload — it renders in
  // every state below so a slow LLM enhancement never delays it.
  const taskQueue = <GuidedTasks projectId={projectId} />;

  if (!evaluationLoaded) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse space-y-3"><div className="h-24 bg-surface-tertiary rounded-2xl" /><div className="h-16 bg-surface-tertiary rounded-xl" /></div>
        {taskQueue}
      </div>
    );
  }

  if (!evaluation) {
    return (
      <div className="space-y-4">
        <ErrorMessage message="The evaluation could not be loaded." onRetry={onRetry} />
        {taskQueue}
      </div>
    );
  }

  const nudges = visibleNudges(evaluation.nudges);

  const tabs = [
    { id: "nudges", label: "Smart Nudges", badge: nudges.length },
    { id: "licenses", label: "Per-License Readiness", badge: evaluation.licenses_total },
  ];

  return (
    <div className="space-y-4">
      {/* Health overview */}
      <div className="flex items-center gap-6 bg-surface border border-border rounded-2xl p-4">
        <HealthRing percentage={evaluation.readiness_pct} size={88} strokeWidth={6} />
        <div className="flex-1 space-y-2">
          {[
            { label: "Fields populated", value: evaluation.fields_complete, total: evaluation.fields_total },
            // No meter without a real denominator — a bar implies a
            // completeness claim the data can't back.
            { label: "Documents uploaded", value: evaluation.docs_uploaded, total: null },
            { label: "Licenses on track", value: evaluation.licenses_on_track, total: evaluation.licenses_total },
          ].map(bar => (
            <div key={bar.label} className="flex items-center gap-3">
              <span className="text-[13px] text-text-secondary w-32">{bar.label}</span>
              {bar.total ? (
                <div className="flex-1 h-1.5 rounded-full bg-surface-tertiary overflow-hidden">
                  <div
                    className="h-full bg-brand rounded-full transition-all"
                    style={{ width: `${(bar.value / bar.total) * 100}%` }}
                  />
                </div>
              ) : (
                <div className="flex-1" />
              )}
              <span className="text-[13px] text-text-tertiary w-12 text-right">
                {bar.value}{bar.total ? `/${bar.total}` : ""}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs — ai accent: this strip switches machine-authored content */}
      <Tabs items={tabs} active={activeTab} onChange={setActiveTab} accent="ai" label="Evaluation sections" idBase="eval-tabs" />

      <div {...tabPanelProps("eval-tabs", activeTab)} className="space-y-4">
      {/* Smart Nudges */}
      {activeTab === "nudges" && (
        <div className="space-y-2">
          {nudges.length === 0 ? (
            <p className="text-[16px] text-text-secondary py-4">No recommendations at this time.</p>
          ) : (
            nudges.map((nudge: any) => (
              <NudgeCard key={nudge.id} nudge={nudge} />
            ))
          )}
        </div>
      )}

      {/* Per-License Readiness */}
      {activeTab === "licenses" && (
        <div className="space-y-1.5">
          {evaluation.per_license_readiness.map((lic: any) => (
            <Link
              key={lic.license_type_id}
              to={`/projects/${projectId}/licenses/${lic.license_type_id}`}
              className="flex items-center gap-3 px-3 py-2.5 bg-surface border border-border rounded-xl card-interactive hover:border-border-secondary no-underline text-text-primary"
            >
              <HealthRing percentage={lic.readiness_pct} size={32} strokeWidth={3} />
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-medium truncate">{lic.license_name}</div>
                <div className="text-[12px] text-text-tertiary">
                  {lic.authority} · {lic.fields_complete}/{lic.fields_total} fields
                </div>
              </div>
              <StatusPill status={lic.status} />
              <ChevronRight size={14} className="text-text-tertiary" />
            </Link>
          ))}
        </div>
      )}
      </div>

      {/* Task queue — the deterministic dependency-graph list, kept alongside
          the LLM-ranked nudges by product decision (2026-07-10): nudges say
          why, the queue says what unlocks what. */}
      {taskQueue}

      {/* Investor Readiness teaser — hidden by feature flag (#16) */}
      {SHOW_BANKABILITY && evaluation.bankability && (
        <BankabilityTeaser bankability={evaluation.bankability} />
      )}
    </div>
  );
}

// ─── Bankability Teaser (demoted from sub-tab) ───

function BankabilityTeaser({ bankability }: { bankability: any }) {
  const [expanded, setExpanded] = useState(false);

  // One truth, several views: label and colours come from the band, so this
  // panel can never disagree with the dashboard about the same score.
  const gradeBand = band(bankability.overall_score, "bankability");
  const gradeLabel = bankabilityLabel(gradeBand);
  const gradeColor = `${bandStyle(gradeBand).bg} ${bandStyle(gradeBand).text}`;

  return (
    <div className="border border-border rounded-2xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-surface-secondary/50 transition-colors"
      >
        <DollarSign size={16} className="text-ai flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-[14px] font-medium">Investor Readiness Preview</span>
          <span className="text-[12px] text-text-tertiary ml-2">
            {bankability.overall_score}% score
          </span>
        </div>
        <span className={`text-[13px] font-medium px-2 py-0.5 rounded-full ${gradeColor}`}>
          {gradeLabel}
        </span>
        <ChevronRight size={14} className={`text-text-tertiary transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-border space-y-3">
          {/* Five-dimension bars */}
          {bankability.dimensions.map((dim: any) => (
            <div key={dim.dimension}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[13px] font-medium">{dim.dimension}</span>
                <span className={`text-[12px] font-medium ${
                  bandText(band(dim.score, "bankability"))
                }`}>{dim.score}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-surface-tertiary overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    bandBar(band(dim.score, "bankability"))
                  }`}
                  style={{ width: `${dim.score}%` }}
                />
              </div>
            </div>
          ))}

          {/* Investor questions */}
          {bankability.investor_questions?.length > 0 && (
            <div>
              <h4 className="text-[12px] font-medium text-text-tertiary uppercase tracking-wider mb-1.5">
                Unanswerable investor questions
              </h4>
              <div className="space-y-1">
                {bankability.investor_questions.map((q: string, i: number) => (
                  <div key={i} className="flex items-start gap-2 px-2.5 py-1.5 bg-warning-light/50 border border-warning/10 rounded-lg text-[13px] text-warning-text">
                    <DollarSign size={12} className="flex-shrink-0 mt-0.5" />
                    <span>{q}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Nudge Card ───

function NudgeCard({ nudge }: { nudge: any }) {
  const colors = status("nudge", nudge.type);

  return (
    <div className={`border-l-[3px] ${colors.accent} ${colors.bg} rounded-r-lg px-3.5 py-3`}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <span className={`text-[12px] font-medium px-1.5 py-0.5 rounded ${colors.text} bg-surface/60`}>
            {nudge.type.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase())}
          </span>
          {nudge.criticality === "critical_path" && (
            <span className="text-[11px] font-medium text-danger">CRITICAL PATH</span>
          )}
        </div>
        <span className={`text-[12px] font-medium ${colors.text} whitespace-nowrap`}>
          {nudge.impact}
        </span>
      </div>

      <h4 className="text-[16px] font-medium mb-1">{nudge.title}</h4>
      <p className="text-[14px] text-text-secondary leading-relaxed mb-2">{nudge.body}</p>

      {/* Field chips */}
      {nudge.field_ids.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {nudge.field_ids.map((fid: string) => (
            <span key={fid} className="font-mono text-[11px] px-1.5 py-0.5 bg-surface/60 rounded text-text-tertiary">
              {fid}
            </span>
          ))}
        </div>
      )}

      {nudge.action_label && nudge.action_href && (
        <Link
          to={nudge.action_href}
          className={`inline-flex items-center gap-1 text-[13px] font-medium ${colors.text} no-underline hover:underline`}
        >
          {nudge.action_label} <ArrowRight size={12} />
        </Link>
      )}
    </div>
  );
}
